<template>
  <div class="admin-shell">
    <BaseLogoBar
      :show-mobile-admin-menu-button="true"
      :admin-menu-open="mobileSidebarOpen"
      @toggle-admin-menu="toggleMobileSidebar"
    />

    <transition name="mobile-drawer-fade">
      <button
        v-if="mobileSidebarOpen"
        type="button"
        class="mobile-drawer-overlay"
        aria-label="Close admin menu"
        @click="closeMobileSidebar"
      ></button>
    </transition>

    <transition name="mobile-drawer-slide">
      <aside v-if="mobileSidebarOpen" class="mobile-drawer" aria-label="Admin mobile navigation">
        <div class="mobile-drawer__panel">
          <div class="mobile-drawer__header">
            <div>
              <span class="mobile-drawer__eyebrow">Admin Menu</span>
              <h2>{{ dashboardTitle }}</h2>
            </div>
            <button
              type="button"
              class="mobile-drawer__close"
              aria-label="Close admin menu"
              @click="closeMobileSidebar"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <div class="mobile-drawer__role">
            <span class="sidebar__role-dot"></span>
            {{ roleLabel }}
          </div>

          <div class="mobile-drawer__nav">
            <button
              v-for="item in visibleMenuItems"
              :key="`mobile-${item.key}`"
              type="button"
              :class="[
                'nav-tile',
                'nav-tile--mobile',
                { 'nav-tile--active': activeTab === item.key },
              ]"
              @click="switchActiveTab(item)"
            >
              <span class="nav-tile__icon-wrap">
                <img
                  v-if="!item.iconClassActive && !item.iconClassInactive"
                  :src="activeTab === item.key ? item.iconActive : item.iconInactive"
                  :alt="item.title"
                  class="nav-tile__icon"
                />
                <i
                  v-else
                  :class="[
                    activeTab === item.key ? item.iconClassActive : item.iconClassInactive,
                    'nav-tile__font-icon',
                  ]"
                  aria-hidden="true"
                ></i>
              </span>
              <span class="nav-tile__copy">
                <span class="nav-tile__title">{{ item.title }}</span>
                <span class="nav-tile__meta">{{ item.description }}</span>
              </span>
            </button>
          </div>
        </div>
      </aside>
    </transition>

    <div class="admin-shell__layout" :style="{ '--admin-sidebar-width': sidebarWidth }">
      <aside
        id="sidebar"
        :class="['sidebar', 'd-none', 'd-md-flex', { 'sidebar--collapsed': sidebarCollapsed }]"
      >
        <div class="sidebar__panel">
          <div class="sidebar__header">
            <div class="sidebar__header-copy">
              <span class="sidebar__eyebrow">Admin Workspace</span>
              <h1>{{ dashboardTitle }}</h1>
            </div>
            <div class="sidebar__header-actions">
              <div class="sidebar__role-badge">
                <span class="sidebar__role-dot"></span>
                {{ roleLabel }}
              </div>
              <button
                type="button"
                class="sidebar__toggle"
                :aria-label="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                @click="toggleSidebar"
              >
                <i :class="sidebarCollapsed ? 'fas fa-angle-right' : 'fas fa-angle-left'"></i>
              </button>
            </div>
          </div>

          <div class="sidebar__nav">
            <button
              v-for="item in visibleMenuItems"
              :key="item.key"
              type="button"
              :class="['nav-tile', { 'nav-tile--active': activeTab === item.key }]"
              @click="switchActiveTab(item)"
            >
              <span class="nav-tile__icon-wrap">
                <img
                  v-if="!item.iconClassActive && !item.iconClassInactive"
                  :src="activeTab === item.key ? item.iconActive : item.iconInactive"
                  :alt="item.title"
                  class="nav-tile__icon"
                />
                <i
                  v-else
                  :class="[
                    activeTab === item.key ? item.iconClassActive : item.iconClassInactive,
                    'nav-tile__font-icon',
                  ]"
                  aria-hidden="true"
                ></i>
              </span>
              <span class="nav-tile__copy">
                <span class="nav-tile__title">{{ item.title }}</span>
                <span class="nav-tile__meta">{{ item.description }}</span>
              </span>
            </button>

            <div class="nav-tile nav-tile--muted">
              <span class="nav-tile__icon-wrap nav-tile__icon-wrap--muted">
                <img
                  src="../../../assets/icons/setting-outline.png"
                  alt="settings"
                  class="nav-tile__icon"
                />
              </span>
              <span class="nav-tile__copy">
                <span class="nav-tile__title">Settings</span>
                <span class="nav-tile__meta">Platform configuration coming soon</span>
              </span>
            </div>
          </div>

          <div class="sidebar__footer">
            <span class="sidebar__footer-label">Workspace</span>
            <p>{{ dashboardDescription }}</p>
          </div>
        </div>
      </aside>

      <div
        :class="['main-content-wrapper', { 'main-content-wrapper--expanded': sidebarCollapsed }]"
      >
        <main class="content">
          <div class="content__frame">
            <router-view class="main-right-view" />
          </div>
          <BaseFooter class="admin-footer" />
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
// import folderWhiteIcon from '../../../assets/icons/folder-white.png';
// import folderOutlineIcon from '../../../assets/icons/folder-outline.png';
import organizationWhiteIcon from '../../../assets/icons/organization-white.png';
import organizationLightIcon from '../../../assets/icons/organization-light.png';
import creditCardWhiteIcon from '../../../assets/icons/credit-card-white.png';
import creditCardOutlineIcon from '../../../assets/icons/credit-card-outline.png';
// import reportWhiteIcon from '../../../assets/icons/report-white.png';
// import reportOutlineIcon from '../../../assets/icons/report-outline.png';
import settingsFilledIcon from '../../../assets/icons/settings-filled.png';
import settingWhiteIcon from '../../../assets/icons/setting-white.png';
// import stakeholderWhiteIcon from '../../../assets/icons/stakeholder-white.png';
// import stakeholderOutlineIcon from '../../../assets/icons/stakeholder-outline.png';
import userWhiteIcon from '../../../assets/icons/user-white.png';
import usersOutlineIcon from '../../../assets/icons/users-outline.png';
import activityWhiteIcon from '../../../assets/icons/activity-white.png';
import activityOutlineIcon from '../../../assets/icons/activity-outline.png';
import helpWhiteIcon from '../../../assets/icons/help-white.png';
import helpOutlineIcon from '../../../assets/icons/help-outline.png';

export default {
  name: 'AdminLayout',
  data() {
    return {
      activeTab: '',
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      menuItems: [
        // {
        //   key: 'project',
        //   title: 'Projects',
        //   description: 'Monitor interventions and research work',
        //   link: '/admin/project',
        //   iconActive: folderWhiteIcon,
        //   iconInactive: folderOutlineIcon,
        //   roles: [1],
        // },
        {
          key: 'organization',
          title: 'NGO Management',
          description: 'Review NGO profiles and registrations',
          link: '/admin/organization',
          iconActive: organizationWhiteIcon,
          iconInactive: organizationLightIcon,
          roles: [1, 2, 6],
        },
        {
          key: 'subscription',
          title: 'Registrations',
          description: 'Track plans and organization access',
          link: '/admin/subscription',
          iconActive: creditCardWhiteIcon,
          iconInactive: creditCardOutlineIcon,
          roles: [1, 2, 6],
        },
        // {
        //   key: 'report',
        //   title: 'Annual Report',
        //   description: 'Review submitted organizational reporting',
        //   link: '/admin/report',
        //   iconActive: reportWhiteIcon,
        //   iconInactive: reportOutlineIcon,
        //   roles: [1, 2, 5],
        // },

        {
          key: 'program-areas',
          title: 'Program Areas',
          description: 'Maintain the shared program-area catalog',
          link: '/admin/program-areas',
          iconActive: settingWhiteIcon,
          iconInactive: settingsFilledIcon,
          roles: [1, 2, 6],
        },
        // {
        //   key: 'stakeholder',
        //   title: 'Stakeholders',
        //   description: 'Organize institutional stakeholder records',
        //   link: '/admin/stakeholder',
        //   iconActive: stakeholderWhiteIcon,
        //   iconInactive: stakeholderOutlineIcon,
        //   roles: [1],
        // },
        {
          key: 'user',
          title: 'User Roles',
          description: 'Assign permissions and access levels',
          link: '/admin/user',
          iconActive: userWhiteIcon,
          iconInactive: usersOutlineIcon,
          roles: [1, 6],
        },
        {
          key: 'activities',
          title: 'Activity Logs',
          description: 'Audit recent platform activity',
          link: '/admin/activities',
          iconActive: activityWhiteIcon,
          iconInactive: activityOutlineIcon,
          roles: [1, 6],
        },
        {
          key: 'notifications',
          title: 'Notifications',
          description: 'Review incoming comment alerts and updates',
          link: '/admin/notifications',
          iconClassActive: 'fas fa-bell',
          iconClassInactive: 'far fa-bell',
          roles: [1, 2, 6],
        },
        {
          key: 'faq',
          title: 'Help & Support',
          description: 'Manage guidance and support content',
          link: '/admin/faq',
          iconActive: helpWhiteIcon,
          iconInactive: helpOutlineIcon,
          roles: [1, 2, 5, 6],
        },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['user']),

    currentUserType() {
      return Number(this.user?.type);
    },

    sidebarWidth() {
      return this.sidebarCollapsed ? '92px' : '340px';
    },

    visibleMenuItems() {
      return this.menuItems.filter((item) => item.roles.includes(this.currentUserType));
    },
    roleLabel() {
      if (this.currentUserType === 6) return 'Admin Management Console';
      if (this.currentUserType === 1) {
        return 'Super Admin Console';
      }
      if (this.currentUserType === 2) {
        return 'NGO Admin Console';
      }
      if (this.currentUserType === 5) {
        return 'Organization Portal';
      }
      return 'Workspace';
    },
    dashboardTitle() {
      if (this.currentUserType === 6) return 'Administrative access';
      if (this.currentUserType === 1) {
        return 'Oversight dashboard';
      }
      if (this.currentUserType === 2) {
        return 'Organization management';
      }
      if (this.currentUserType === 5) {
        return 'Reporting center';
      }
      return 'Admin dashboard';
    },
    dashboardDescription() {
      if (this.currentUserType === 6) return 'Manage administrator accounts and platform access.';
      if (this.currentUserType === 1) {
        return 'A refined control surface for platform operations, data governance, and system visibility.';
      }
      if (this.currentUserType === 2) {
        return 'Review organization activity, approvals, and subscription operations from one polished workspace.';
      }
      if (this.currentUserType === 5) {
        return 'Stay on top of annual reporting, submissions, and organization compliance requirements.';
      }
      return 'A central workspace for platform administration.';
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        this.syncActiveTabWithRoute();
      },
    },
  },
  beforeMount() {
    if (![1, 2, 5, 6].includes(this.currentUserType)) {
      this.$router.push('/');
    }
  },
  mounted() {
    this.sidebarCollapsed = localStorage.getItem('admin_sidebar_collapsed') === 'true';
    this.redirectToDefaultRoute();
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      localStorage.setItem('admin_sidebar_collapsed', String(this.sidebarCollapsed));
    },

    switchActiveTab(item) {
      this.activeTab = item.key;
      this.closeMobileSidebar();
      if (this.$route.path !== item.link) {
        this.$router.push(item.link);
      }
    },
    toggleMobileSidebar() {
      this.mobileSidebarOpen = !this.mobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.mobileSidebarOpen = false;
    },
    redirectToDefaultRoute() {
      if (this.$route.path !== '/admin' && this.$route.path !== '/admin/') {
        return;
      }

      if (this.currentUserType === 6) {
        this.$router.push('/admin/user');
        return;
      }

      if (this.currentUserType === 1) {
        this.$router.push('/admin/project');
        return;
      }

      if (this.currentUserType === 2) {
        this.$router.push('/admin/organization');
        return;
      }

      if (this.currentUserType === 5) {
        this.$router.push('/admin/report');
      }
    },
    syncActiveTabWithRoute() {
      this.closeMobileSidebar();
      const matchedItem = this.menuItems.find((item) => {
        if (item.link === '/submit_yearly_report') {
          return this.$route.path === item.link;
        }

        return this.$route.path === item.link || this.$route.path.startsWith(`${item.link}/`);
      });

      this.activeTab = matchedItem ? matchedItem.key : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-shell {
  min-height: 100vh;
  background: radial-gradient(circle at top left, rgba(42, 138, 67, 0.1), transparent 32%),
    linear-gradient(180deg, #eef6f0 0%, #f8fbf9 22%, #fdfefd 100%);
}

.admin-shell__layout {
  display: grid;
  grid-template-columns: var(--admin-sidebar-width) minmax(0, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  padding: 104px 16px 16px;
}

.mobile-drawer,
.mobile-drawer-overlay {
  display: none;
}

.mobile-drawer__close {
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
}

.mobile-drawer {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 1080;
  width: min(88vw, 360px);
}

.mobile-drawer__panel {
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 1.1rem 1rem 1.25rem;
  background: linear-gradient(180deg, rgba(251, 253, 251, 0.98), rgba(240, 246, 242, 0.98)), #ffffff;
  box-shadow: 18px 0 40px rgba(11, 35, 20, 0.2);
}

.mobile-drawer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 5rem 0 0.9rem;
}

.mobile-drawer__eyebrow {
  display: block;
  margin-bottom: 0.4rem;
  color: #2a8a43;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.mobile-drawer__header h2 {
  margin: 0;
  color: #193624;
  font-size: 1.2rem;
  line-height: 1.2;
}

.mobile-drawer__close {
  width: 42px;
  height: 42px;
  min-width: 42px;
  background: rgba(42, 138, 67, 0.1);
  color: #226f39;
}

.mobile-drawer__role {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  align-self: flex-start;
  margin-bottom: 1rem;
  padding: 0.5rem 0.8rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.1);
  color: #226f39;
  font-size: 0.72rem;
  font-weight: 700;
}

.mobile-drawer__nav {
  flex: 1;
  overflow-y: auto;
  padding-right: 0.15rem;
}

.nav-tile--mobile {
  margin-bottom: 0.65rem;
}

.mobile-drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 1070;
  border: 0;
  background: rgba(8, 20, 12, 0.48);
}

.sidebar {
  position: relative;
  width: 100%;
  max-width: var(--admin-sidebar-width);
  min-width: 0;
  align-self: start;
  overflow-x: hidden;
}

.sidebar__panel {
  position: fixed;
  top: 104px;
  left: 16px;
  display: flex;
  flex-direction: column;
  width: calc(var(--admin-sidebar-width) - 0px);
  max-width: calc(100vw - 32px);
  min-width: 0;
  height: calc(100vh - 120px);
  padding: 1.1rem;
  box-sizing: border-box;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(247, 250, 248, 0.94)), #ffffff;
  border: 1px solid rgba(29, 70, 44, 0.08);
  box-shadow: 0 12px 28px rgba(21, 55, 33, 0.05);
}

.sidebar__header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.9rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(29, 70, 44, 0.08);
}

.sidebar__header-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
}

.sidebar__header-copy {
  min-width: 0;
}

.sidebar__eyebrow,
.sidebar__footer-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #2a8a43;
}

.sidebar__header h1 {
  margin: 0.4rem 0 0;
  font-size: 1.12rem;
  line-height: 1.15;
  color: #193624;
}

.sidebar__footer p {
  margin: 0;
  font-size: 0.76rem;
  line-height: 1.45;
  color: #678174;
}

.sidebar__role-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.65rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.1);
  color: #226f39;
  font-size: 0.66rem;
  font-weight: 700;
  white-space: normal;
  line-height: 1.35;
  max-width: 100%;
}

.sidebar__role-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #2fa049, #1d6a33);
  box-shadow: 0 0 0 6px rgba(47, 160, 73, 0.12);
}

.sidebar__toggle {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border: none;
  border-radius: 12px;
  background: rgba(42, 138, 67, 0.12);
  color: #226f39;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sidebar__nav {
  flex: 1;
  margin-top: 0.8rem;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.1rem;
  min-width: 0;
}

.nav-tile {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.8rem 0.9rem;
  margin-bottom: 0.5rem;
  border: 1px solid rgba(29, 70, 44, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
  text-align: left;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease;
}

.nav-tile:hover {
  border-color: rgba(42, 138, 67, 0.22);
  box-shadow: 0 8px 18px rgba(20, 55, 31, 0.05);
}

.nav-tile--active {
  background: linear-gradient(135deg, #1f6f33, #31a54c);
  border-color: transparent;
  box-shadow: 0 10px 22px rgba(31, 111, 51, 0.15);
}

.nav-tile--muted {
  cursor: default;
  opacity: 0.82;
}

.nav-tile--muted:hover {
  transform: none;
  border-color: rgba(29, 70, 44, 0.08);
  box-shadow: none;
}

.nav-tile__icon-wrap {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 12px;
  background: rgba(42, 138, 67, 0.1);
}

.nav-tile--active .nav-tile__icon-wrap {
  background: rgba(255, 255, 255, 0.18);
}

.nav-tile__icon-wrap--muted {
  background: rgba(100, 120, 108, 0.1);
}

.nav-tile__icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.nav-tile__font-icon {
  font-size: 1rem;
  color: #2a8a43;
}

.nav-tile__copy {
  display: flex;
  flex-direction: column;
  gap: 0.14rem;
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.nav-tile__title {
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.2;
  color: #183523;
  word-break: break-word;
}

.nav-tile__meta {
  font-size: 0.68rem;
  line-height: 1.35;
  color: #6b8477;
  word-break: break-word;
}

.nav-tile--active .nav-tile__title,
.nav-tile--active .nav-tile__meta,
.nav-tile--active .nav-tile__font-icon {
  color: #ffffff;
}

.sidebar__footer {
  margin-top: 0.65rem;
  padding: 0.8rem 0.85rem;
  border-radius: 16px;
  background: rgba(238, 246, 240, 0.7);
  border: 1px solid rgba(29, 70, 44, 0.06);
}

.main-content-wrapper {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.sidebar--collapsed {
  width: 100%;
  max-width: 92px;
  min-width: 0;
}

.sidebar--collapsed .sidebar__panel {
  padding: 0.85rem 0.65rem;
}

.sidebar--collapsed .sidebar__header-copy,
.sidebar--collapsed .sidebar__role-badge,
.sidebar--collapsed .sidebar__footer,
.sidebar--collapsed .nav-tile__copy {
  display: none;
}

.sidebar--collapsed .sidebar__header {
  justify-content: center;
  align-items: center;
  padding-bottom: 0.7rem;
}

.sidebar--collapsed .sidebar__header-actions {
  width: auto;
  justify-content: center;
}

.sidebar--collapsed .sidebar__nav {
  margin-top: 0;
  padding-right: 0;
}

.sidebar--collapsed .nav-tile {
  justify-content: center;
  padding: 0.7rem 0.45rem;
}

.main-content-wrapper--expanded {
  width: 100%;
}

.content {
  min-height: calc(100vh - 120px);
  padding: 0;
  width: 100%;
}

.content__frame {
  min-height: calc(100vh - 170px);
  padding: 0 0 16px;
  border-radius: 24px;
}

.admin-footer {
  width: 100%;
}

@media (max-width: 991px) {
  .admin-shell__layout {
    display: block;
    padding: 112px 10px 12px;
  }

  .mobile-drawer,
  .mobile-drawer-overlay {
    display: block;
  }

  .sidebar {
    display: none !important;
  }

  .sidebar__toggle {
    display: none;
  }

  .main-content-wrapper {
    width: 100%;
    margin-left: 0;
  }

  .content {
    min-height: auto;
  }
}

@media (max-width: 600px) {
  .admin-shell__layout {
    padding-top: 104px;
  }

  .mobile-drawer {
    width: min(92vw, 360px);
  }

  .mobile-drawer__panel {
    padding-inline: 0.9rem;
  }

  .mobile-drawer__header {
    padding-top: 4.85rem;
  }
}

.mobile-drawer-fade-enter-active,
.mobile-drawer-fade-leave-active,
.mobile-drawer-slide-enter-active,
.mobile-drawer-slide-leave-active {
  transition: all 0.22s ease;
}

.mobile-drawer-fade-enter,
.mobile-drawer-fade-leave-to {
  opacity: 0;
}

.mobile-drawer-slide-enter,
.mobile-drawer-slide-leave-to {
  opacity: 0;
  transform: translateX(-18px);
}
</style>
